import Config from '../../../config';
let config;
import features from 'lib/common/features';
import TessituraAPI from './tessitura-wrapper';
import fallbacks from 'lib/common/fallbacks';
import logger from 'lib/common/logger';
import { enhanceSeatData, setScreenZoneData } from 'lib/common/transforms';

import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import indexOf from 'lodash/indexOf';
import forEach from 'lodash/forEach';

import PerformanceMap from 'lib/tessitura/REST/maps/Performance';
import PackageMap from 'lib/tessitura/REST/maps/Package';
import ScreenMap from 'lib/tessitura/REST/maps/Screen';
import SectionMap from 'lib/tessitura/REST/maps/Section';
import SeatMap from 'lib/tessitura/REST/maps/Seat';
import SeatStatusMap from 'lib/tessitura/REST/maps/SeatStatus';
import CartMap from 'lib/tessitura/REST/maps/Cart';

let API;
let INITIAL_SESSION_KEY;

const globalPromises = {};

export function configure(endpoint, initialSessionKey) {
  let apiInstance = new TessituraAPI(endpoint, initialSessionKey);
  API = apiInstance;

  if (initialSessionKey) {
    setInitialSessionKey(initialSessionKey);
  }
}

export function getInitialSessionKey() {
  return INITIAL_SESSION_KEY || '%sessionKey%';
}

export function setInitialSessionKey(sessionKey) {
  INITIAL_SESSION_KEY = sessionKey;
  return INITIAL_SESSION_KEY;
}

export function getSessionKey() {
  let initialSessionKey = getInitialSessionKey();
  if (initialSessionKey) {
    return Promise.resolve(initialSessionKey);
  } else {
    return API.getSessionKey();
  }
}

export function loginInfo() {
  return API.requestGet({
    method: `Web/Session/${getInitialSessionKey()}`
  }).then(response => {
    if (has(response, 'result.ModeOfSaleId')) {
      return {
        MOS: response.result.ModeOfSaleId,
        promotion_code: response.result.SourceId
      };
    } else {
      logger.captureMessage('Failed to load LoginInfoEx');
      return false;
    }
  });
}

export function StoreADAData(data) {
  return API.requestPut({
    method: `Web/Cart/%sessionKey%/Properties`,
    params: {
      Notes: data
    }
  });
}

export function getPromoCode(promoCode, sourceCode) {
  return API.request({
    method: `Web/Session/%sessionKey%/PromoCode`,
    params: {
      PromoCodeString: promoCode,
      PromoCode: sourceCode
    }
  });
}

export function removePromoCode() {
  // Update the source number and mode of sale, then refresh
  return API.requestPut({
    method: 'Web/Cart/%sessionKey%/Properties',
    params: {
      SourceId: features.getOption('promoCode', 'defaultSourceNo'),
      ModeOfSaleId: features.getOption('promoCode', 'defaultModeOfSaleId')
    }
  }).then(() => {
    window.location.reload();
    return true;
  });
}

export function addPromoCode(promoCode) {
  return getPromoCode(promoCode).then(response => {
    // Update the source number and mode of sale, then refresh

    return API.requestPut({
      method: 'Web/Cart/%sessionKey%/Properties',
      params: {
        SourceId: response.result[0].Source.Id,
        ModeOfSaleId: response.result[0].ModeOfSaleId
      }
    }).then(() => {
      window.location.reload();
      return true;
    });
  });
}

// @TODO: Decouple this method
export function getSeatmapData(performanceId, packageNo, currentMOS, venueId) {
  let promises = [];

  /**
   * By default GetSeatsBriefEx requires a session key, meaning each user
   * gets a unique cache hit, so the cache only works for subsequent calls.
   *
   * If cacheableGetSeats is enabled, assuming a GetSeatsBriefExMOS method,
   * we swap out the get seats call out with one that doesn't require
   * a session key; in place we need to pass MOS and source code.
   *
   * This allows us to cache the call in a non-unique way,
   * which greatly increases the value of the cache.
   */
  if (packageNo) {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: `TXN/Packages/${packageNo}/Seats`,
        params: {
          packageId: packageNo,
          modeOfSaleId: currentMOS,
          constituentId: 0
        }
      })
    ]);
  } else {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: `TXN/Performances/${performanceId}/Seats`,
        params: {
          performanceId: packageNo ? -1 : performanceId,
          modeOfSaleId: currentMOS,
          constituentId: 0
        }
      })
    ]);
  }

  promises = promises.concat([
    API.requestGet({
      method: 'ReferenceData/SeatStatuses',
      params: {}
    })
  ]);

  if (!packageNo) {
    promises = promises.concat([
      getScreensForPerformance(venueId),
      getSectionsForPerformance(venueId)
    ]);
  } else {
    promises = promises.concat([
      getScreensForPackage(venueId),
      getSectionsForPackage(venueId)
    ]);
  }

  return Promise.all(promises)
    .then(function(responses) {
      let seatsBriefResults = get(responses[0], 'result');
      let seatStatusResults = get(responses[1], 'result');
      let screensResults = get(responses[2], 'result');
      let seatsWithZones = get(responses[3], 'result');

      let seatsMap = new SeatMap();
      let statusMap = new SeatStatusMap();
      let screenMap = new ScreenMap();
      let sectionMap = new SectionMap();

      let seatsResults = enhanceSeatData(
        seatsMap.map(seatsBriefResults, true),
        statusMap.map(seatStatusResults),
        sectionMap.map(seatsWithZones),
        screensResults === undefined
          ? screenMap.map()
          : screenMap.map(screensResults.Screens)
      );

      let screenZoneData =
        seatsBriefResults && setScreenZoneData(seatsMap.map(seatsBriefResults));

      return {
        screensResults: {
          Screens:
            screensResults === undefined
              ? screenMap.map()
              : screenMap.map(screensResults.Screens)
        },
        seatsStatusResults: seatStatusResults,
        seatsResults: seatsResults,
        screenZoneData: screenZoneData
      };
    })
    .catch(fallbacks.redirectWithError);
}

export function getScreensForPerformance(venueId) {
  return API.requestGet({
    method: `TXN/Facilities/${venueId}/Screens`
  });
}

export function getSectionsForPerformance(venueId) {
  return API.requestGet({
    method: 'ReferenceData/Sections',
    params: {
      seatMapId: venueId
    }
  });
}

export function getScreensForPackage(venueId) {
  return API.requestGet({
    method: `TXN/Facilities/${venueId}/Screens`
  });
}

export function getSectionsForPackage(venueId) {
  return API.requestGet({
    method: 'ReferenceData/Sections',
    params: {
      seatMapId: venueId
    }
  });
}

export function getMaxTicketAmount() {
  config = new Config().getConfig();
  if (config.customMaxTicketsLimit) {
    return Promise.resolve(config.customMaxTicketsLimit);
  } else {
    return Promise.resolve(10);
  }
}

export function getDetailsWithDiscounting(
  performanceId,
  packageNo,
  modeOfSale,
  sourceCode
) {
  if (packageNo) {
    let promises = [
      API.requestGet({
        method: `TXN/Packages/${packageNo}`
      }),
      API.requestGet({
        method: `TXN/Packages/${packageNo}/Prices`,
        params: {
          modeOfSaleId: modeOfSale,
          priceTypeId: '',
          expandPerformancePriceType: '',
          includeOnlyBasePrice: '',
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: 'TXN/PriceTypes/Details',
        params: {
          packageId: packageNo,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: 'TXN/Performances/ZoneAvailabilities',
        params: {
          performanceIds: performanceId
        }
      }),
      API.requestGet({
        method: 'TXN/WebContents',
        params: {
          packageIds: packageNo
        }
      })
    ];

    return Promise.all(promises)
      .then(responses => {
        let pkgMap = new PackageMap();
        let mapped = pkgMap.map(responses);
        return mapped;
      })
      .catch(fallbacks.redirectWithError);
  } else {
    let promises = [
      API.requestGet({
        method: `TXN/Performances/${performanceId}`
      }),
      API.requestGet({
        method: 'TXN/Performances/Prices',
        params: {
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          priceTypeId: '',
          expandPerformancePriceType: '',
          includeOnlyBasePrice: '',
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: 'TXN/PriceTypes/Details',
        params: {
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: 'TXN/Performances/ZoneAvailabilities',
        params: {
          performanceIds: performanceId
        }
      }),
      API.requestGet({
        method: 'TXN/WebContents',
        params: {
          productionElementIds: performanceId
        }
      })
    ];

    return Promise.all(promises).then(responses => {
      let map = new PerformanceMap();
      return map.map(responses);
    });
  }
}

export function getProductionDetail(performanceId) {
  return API.requestGet({
    method: 'TXN/Performances/Summary',
    params: {
      performanceIds: performanceId
    }
  }).then(response => {
    let perfMap = new PerformanceMap();
    let result = response.result.map(item => {
      return perfMap.mapPerformance([{ result: item }]);
    });

    return {
      result: result
    };
  });
}

export function getAllPerformances(prodSeasonNo) {
  return API.requestGet({
    method: 'TXN/Performances',
    params: {
      productionSeasonId: prodSeasonNo
    }
  }).then(response => {
    let perfMap = new PerformanceMap();
    let result = response.result.map(item => {
      return perfMap.mapPerformance([{ result: item }]);
    });

    return {
      result: result
    };
  });
}

export function getCart() {
  return API.requestGet({
    method: 'Web/Cart/%sessionKey%',
    params: {}
  })
    .then(function(response) {
      if (response) {
        let result = response.result;
        let map = new CartMap();
        return !isEmpty(result) ? map.map(result) : false;
      } else {
        return false;
      }
    })
    .catch(function(error) {
      console.log(error);
    });
}

export function ReserveTicketsEx(
  performanceId,
  packageNo,
  zone,
  priceTypes,
  numberOfSeats,
  numOfAccessibleSeats,
  regularBookingHoldCode = 0,
  unseated = false
) {
  let leaveSingleSeats = config.specialRequests.LeaveSingleSeats
    ? config.specialRequests.LeaveSingleSeats
    : 'Y';
  let specialRequest = `LeaveSingleSeats=${leaveSingleSeats}`;
  let accessibleHoldCode = features.getOption(
    'accessibilitySeats',
    'holdCodes'
  );

  if (packageNo > 0) {
    if (numOfAccessibleSeats > 0) {
      return API.request({
        method: 'Web/Cart/%sessionKey%/Packages/Fixed',
        params: {
          PriceType: priceTypes.slice(0, numOfAccessibleSeats).join(','),
          PackageId: packageNo,
          NumberOfSeats: numOfAccessibleSeats,
          SpecialRequests: `${specialRequest}&WheelChairSeats=${numOfAccessibleSeats}`,
          RequestedSeats: '',
          Unseated: unseated,
          ZoneId: zone
        }
      }).then(responseReservedADA => {
        if (responseReservedADA.result.SeatsReserved === numberOfSeats) {
          return {
            result: [0, responseReservedADA.result.SeatsReserved]
          };
        }

        if (responseReservedADA.result.SeatsReserved) {
          return reserveRegularPackageTickets(
            packageNo,
            zone,
            priceTypes.slice(numOfAccessibleSeats, numberOfSeats),
            numberOfSeats,
            numOfAccessibleSeats,
            responseReservedADA.result.SeatsReserved,
            responseReservedADA.result.LineItemId,
            specialRequest,
            regularBookingHoldCode,
            unseated
          );
        } else if (accessibleHoldCode.length > 0) {
          return API.request({
            method: 'Web/Cart/%sessionKey%/Packages/Fixed',
            params: {
              PriceType: priceTypes.slice(0, numOfAccessibleSeats).join(','),
              PackageId: packageNo,
              NumberOfSeats: numOfAccessibleSeats,
              SpecialRequests: `${specialRequest}&HoldCode=${
                accessibleHoldCode[0]
              }`,
              RequestedSeats: '',
              Unseated: unseated,
              ZoneId: zone
            }
          }).then(secondResponseReservedADA => {
            if (
              secondResponseReservedADA.result.SeatsReserved === numberOfSeats
            ) {
              return {
                result: [0, secondResponseReservedADA.result.SeatsReserved]
              };
            }

            if (!secondResponseReservedADA.result.SeatsReserved) {
              return {
                result: [0, 0]
              };
            }

            return reserveRegularPackageTickets(
              packageNo,
              zone,
              priceTypes.slice(numOfAccessibleSeats, numberOfSeats),
              numberOfSeats,
              numOfAccessibleSeats,
              secondResponseReservedADA.result.SeatsReserved,
              secondResponseReservedADA.result.LineItemId,
              specialRequest,
              regularBookingHoldCode,
              unseated
            );
          });
        } else {
          return {
            result: [0, 0]
          };
        }
      });
    } else {
      return reserveRegularPackageTickets(
        packageNo,
        zone,
        priceTypes,
        numberOfSeats,
        0,
        0,
        0,
        specialRequest,
        regularBookingHoldCode,
        unseated
      );
    }
  } else {
    if (numOfAccessibleSeats > 0) {
      return API.request({
        method: 'Web/Cart/%sessionKey%/Tickets',
        params: {
          PriceType: priceTypes.slice(0, numOfAccessibleSeats).join(','),
          PerformanceId: performanceId,
          NumberOfSeats: numOfAccessibleSeats,
          SpecialRequests: `${specialRequest}&WheelChairSeats=${numOfAccessibleSeats}`,
          RequestedSeats: '',
          Unseated: unseated,
          ZoneId: zone
        }
      }).then(responseReservedADA => {
        if (responseReservedADA.result.SeatsReserved === numberOfSeats) {
          return {
            result: [0, responseReservedADA.result.SeatsReserved]
          };
        }

        if (responseReservedADA.result.SeatsReserved) {
          return reserveRegularTickets(
            performanceId,
            zone,
            priceTypes.slice(numOfAccessibleSeats, numberOfSeats),
            numberOfSeats,
            numOfAccessibleSeats,
            responseReservedADA.result.SeatsReserved,
            responseReservedADA.result.LineItemId,
            specialRequest,
            regularBookingHoldCode,
            unseated
          );
        } else if (accessibleHoldCode.length > 0) {
          return API.request({
            method: 'Web/Cart/%sessionKey%/Tickets',
            params: {
              PriceType: priceTypes.slice(0, numOfAccessibleSeats).join(','),
              PerformanceId: performanceId,
              NumberOfSeats: numOfAccessibleSeats,
              SpecialRequests: `${specialRequest}&HoldCode=${
                accessibleHoldCode[0]
              }`,
              RequestedSeats: '',
              Unseated: unseated,
              ZoneId: zone
            }
          }).then(secondResponseReservedADA => {
            if (
              secondResponseReservedADA.result.SeatsReserved === numberOfSeats
            ) {
              return {
                result: [0, secondResponseReservedADA.result.SeatsReserved]
              };
            }

            if (!secondResponseReservedADA.result.SeatsReserved) {
              return {
                result: [0, 0]
              };
            }

            return reserveRegularTickets(
              performanceId,
              zone,
              priceTypes.slice(numOfAccessibleSeats, numberOfSeats),
              numberOfSeats,
              numOfAccessibleSeats,
              secondResponseReservedADA.result.SeatsReserved,
              secondResponseReservedADA.result.LineItemId,
              specialRequest,
              regularBookingHoldCode,
              unseated
            );
          });
        } else {
          return {
            result: [0, 0]
          };
        }
      });
    } else {
      return reserveRegularTickets(
        performanceId,
        zone,
        priceTypes,
        numberOfSeats,
        0,
        0,
        0,
        specialRequest,
        regularBookingHoldCode,
        unseated
      );
    }
  }
}

function reserveRegularTickets(
  performanceId,
  zone,
  priceTypes,
  numberOfSeats,
  numOfAccessibleSeats,
  reservedADASeats,
  reservedADALineItem,
  specialRequest,
  regularBookingHoldCode = 0,
  unseated = false
) {
  let reservedRegularSeats = 0;

  return API.request({
    method: 'Web/Cart/%sessionKey%/Tickets',
    params: {
      PriceType: priceTypes.join(','),
      PerformanceId: performanceId,
      NumberOfSeats: numberOfSeats - numOfAccessibleSeats,
      SpecialRequests:
        regularBookingHoldCode > 0
          ? `${specialRequest}&ContiguousSeats=1&HoldCode=${regularBookingHoldCode}`
          : specialRequest,
      RequestedSeats: '',
      Unseated: unseated,
      ZoneId: zone
    }
  }).then(responseReservedSeats => {
    reservedRegularSeats = responseReservedSeats.result.SeatsReserved;

    if (numberOfSeats - numOfAccessibleSeats > 0 && !reservedRegularSeats) {
      if (regularBookingHoldCode > 0) {
        return API.request({
          method: 'Web/Cart/%sessionKey%/Tickets',
          params: {
            PriceType: priceTypes.join(','),
            PerformanceId: performanceId,
            NumberOfSeats: numberOfSeats - numOfAccessibleSeats,
            SpecialRequests: specialRequest,
            RequestedSeats: '',
            Unseated: unseated,
            ZoneId: zone
          }
        }).then(responseReservedSeats => {
          reservedRegularSeats = responseReservedSeats.result.SeatsReserved;

          if (
            numberOfSeats - numOfAccessibleSeats > 0 &&
            !reservedRegularSeats
          ) {
            reservedRegularSeats = 0;
            reservedADASeats = 0;

            if (reservedADALineItem) {
              ReleaseTickets(performanceId, reservedADALineItem, 0);
            }
          }

          return {
            result: [reservedRegularSeats, reservedADASeats]
          };
        });
      } else {
        if (numberOfSeats - numOfAccessibleSeats > 0 && !reservedRegularSeats) {
          reservedRegularSeats = 0;
          reservedADASeats = 0;

          if (reservedADALineItem) {
            ReleaseTickets(performanceId, reservedADALineItem, 0);
          }
        }
      }
    }

    return {
      result: [reservedRegularSeats, reservedADASeats]
    };
  });
}

function reserveRegularPackageTickets(
  packageId,
  zone,
  priceTypes,
  numberOfSeats,
  numOfAccessibleSeats,
  reservedADASeats,
  reservedADALineItem,
  specialRequest,
  regularBookingHoldCode = 0,
  unseated = false
) {
  let reservedRegularSeats = 0;

  return API.request({
    method: 'Web/Cart/%sessionKey%/Packages/Fixed',
    params: {
      PriceType: priceTypes.join(','),
      PackageId: packageId,
      NumberOfSeats: numberOfSeats - numOfAccessibleSeats,
      SpecialRequests:
        regularBookingHoldCode > 0
          ? `${specialRequest}&ContiguousSeats=1&HoldCode=${regularBookingHoldCode}`
          : specialRequest,
      RequestedSeats: '',
      Unseated: unseated,
      ZoneId: zone
    }
  }).then(responseReservedSeats => {
    reservedRegularSeats = responseReservedSeats.result.SeatsReserved;

    if (numberOfSeats - numOfAccessibleSeats > 0 && !reservedRegularSeats) {
      if (regularBookingHoldCode > 0) {
        return API.request({
          method: 'Web/Cart/%sessionKey%/Packages/Fixed',
          params: {
            PriceType: priceTypes.join(','),
            PackageId: packageId,
            NumberOfSeats: numberOfSeats - numOfAccessibleSeats,
            SpecialRequests: specialRequest,
            RequestedSeats: '',
            Unseated: unseated,
            ZoneId: zone
          }
        }).then(responseReservedSeats => {
          reservedRegularSeats = responseReservedSeats.result.SeatsReserved;

          if (
            numberOfSeats - numOfAccessibleSeats > 0 &&
            !reservedRegularSeats
          ) {
            reservedRegularSeats = 0;
            reservedADASeats = 0;

            if (reservedADALineItem) {
              ReleaseTickets(0, reservedADALineItem, packageId);
            }
          }

          return {
            result: [reservedRegularSeats, reservedADASeats]
          };
        });
      } else {
        reservedRegularSeats = 0;
        reservedADASeats = 0;

        if (reservedADALineItem) {
          ReleaseTickets(0, reservedADALineItem, packageId);
        }
      }
    }

    return {
      result: [reservedRegularSeats, reservedADASeats]
    };
  });
}

export function ReserveTicketsSpecifiedSeats(
  performanceId,
  lineItems,
  fixedPackage
) {
  config = new Config().getConfig();
  let wheelChairSeats = filter(lineItems, item => {
    return (
      indexOf(
        config.specialRequests.excludeSeatType,
        parseInt(item.seatData.seat_type)
      ) > -1 ||
      indexOf(
        config.specialRequests.excludeHoldCode,
        parseInt(item.seatData.hc_no)
      ) > -1
    );
  });

  let leaveSingleSeats = config.specialRequests.LeaveSingleSeats
    ? config.specialRequests.LeaveSingleSeats
    : 'Y';

  if (wheelChairSeats.length) {
    leaveSingleSeats = 'Y';
  }

  if (fixedPackage) {
    return API.request({
      method: 'Web/Cart/%sessionKey%/Packages/Fixed',
      params: {
        // TODO: map these before passing through
        PriceType: lineItems.map(item => item.priceData.price_type).join(','),
        PackageId: fixedPackage,
        NumberOfSeats: lineItems.length,
        SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
        RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
        ZoneId: 0
      }
    }).then(response => {
      return {
        result: response.result.SeatsReserved
      };
    });
  } else {
    return API.request({
      method: 'Web/Cart/%sessionKey%/Tickets',
      params: {
        // TODO: map these before passing through
        PriceType: lineItems.map(item => item.priceData.price_type).join(','),
        PerformanceId: performanceId,
        NumberOfSeats: lineItems.length,
        SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
        RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
        ZoneId: 0
      }
    }).then(response => {
      return {
        result: response.result.SeatsReserved
      };
    });
  }
}

export function ReleaseTickets(
  iPerformanceNumber,
  iLineItemNumber,
  currentFixedPackage
) {
  const method = currentFixedPackage
    ? `Web/Cart/%sessionKey%/Packages/Fixed/${currentFixedPackage}/${iLineItemNumber}`
    : `Web/Cart/%sessionKey%/Tickets/${iPerformanceNumber}/${iLineItemNumber}`;

  return globalPromises[method]
    ? globalPromises[method]
    : (globalPromises[method] = API.request({
        method: method,
        params: {}
      }));
}

export function ReleaseMultipleTickets(iPerformanceNumber, lineItems) {
  let promises = [];

  //lineItems.forEach(item => {
  forEach(lineItems, item => {
    promises = promises.concat([
      API.request({
        method: `Web/Cart/%sessionKey%/Tickets/${iPerformanceNumber}/${
          item.li.no
        }`,
        params: {}
      })
    ]);
  });

  return Promise.all(promises).then(responses => {
    return responses;
  });
}

export function AddPackageItemSeated(currentFixedPackage, lineItems) {
  var priceTypes = lineItems.map(item => item.priceData.price_type).join(',');
  var requestedSeats = lineItems.map(item => item.seatData.seat_no).join(',');

  return API.request({
    method: 'Web/Cart/%sessionKey%/Packages/Fixed',
    params: {
      // TODO: map these before passing through
      PriceType: priceTypes,
      PackageId: currentFixedPackage.pkg_no,
      NumberOfSeats: lineItems.length,
      SpecialRequests: 'LeaveSingleSeats=Y',
      RequestedSeats: requestedSeats,
      ZoneId: 0
    }
  }).then(response => {
    return {
      result: response.result.SeatsReserved
    };
  });
}

export function RemovePackageItem(pkgNo, liSeqNo) {
  return API.request({
    method: `Web/Cart/%sessionKey%/Packages/Fixed/${pkgNo}/${liSeqNo}`,
    params: {}
  });
}

/**
 * Custom Method (GetCurrentPromoCode)
 */
export function GetCurrentPromoCode() {
  return API.requestGet({
    method: 'Web/Session/%sessionKey%'
  }).then(response => {
    return getPromoCode('', response.result.SourceId).then(response => {
      if (response.result.constructor === Array) {
        if (response.result[0] !== undefined) {
          response.result = response.result[0].PromoCode;
        } else {
          response.result = false;
        }
      }
      return response;
    });
  });
}

/**
 * Custom Method (SeatUpsells)
 */
export function SeatUpsells() {
  return API.requestGet({
    method: 'SeatUpsells',
    params: {}
  }).then(function(response) {
    let result = get(response, 'result.GetLocalDataResults.LocalData');
    return result;
  });
}

export function getPerfPricingRuleLookahead(performanceId, modeOfSale) {
  let promise;
  promise = API.requestGet({
    method: 'GetPerfPricingRuleLookahead',
    params: {
      SessionKey: '',
      perf_no: performanceId,
      iModeOfSale: modeOfSale
    }
  }).then(response => get(response, 'result'));

  return promise.catch(fallbacks.redirectWithError);
}

export function getPkgPricingRuleLookahead(packageId, modeOfSale) {
  let promise;
  promise = API.requestGet({
    method: 'GetPkgPricingRuleLookahead',
    params: {
      SessionKey: '',
      pkg_no: packageId,
      iModeOfSale: modeOfSale
    }
  }).then(response => get(response, 'result'));

  return promise.catch(fallbacks.redirectWithError);
}

export default {
  configure,
  getInitialSessionKey,
  setInitialSessionKey,
  getSessionKey,
  loginInfo,
  getSeatmapData,
  getMaxTicketAmount,
  getDetailsWithDiscounting,
  getProductionDetail,
  getAllPerformances,
  getCart,
  ReserveTicketsEx,
  ReserveTicketsSpecifiedSeats,
  ReleaseTickets,
  ReleaseMultipleTickets,
  AddPackageItemSeated,
  RemovePackageItem,
  GetCurrentPromoCode,
  SeatUpsells,
  addPromoCode,
  removePromoCode,
  StoreADAData,
  getPerfPricingRuleLookahead,
  getPkgPricingRuleLookahead
};



// WEBPACK FOOTER //
// ./src/javascripts/lib/tessitura/REST/API.js